<script setup>
import { ref, watch, onMounted } from 'vue';
import ReviewCard from '~/components/review/ReviewCard.vue';
import ReviewFilterBar from '~/components/review/ReviewFilterBar.vue';
import CreateReviewModal from '~/components/review/CreateReviewModal.vue';

definePageMeta({
  title: 'Food Reviews & Feed - Aanza',
});

const auth = useAuthStore();
const { addToast } = useToast();
const { fetchReviews, reactToReview, deleteReview } = useReviewApi();

// State
const reviews = ref([]);
const isLoading = ref(true);
const isLoadingMore = ref(false);
const currentPage = ref(1);
const lastPage = ref(1);
const totalReviews = ref(0);

// Filters
const search = ref('');
const activeTag = ref('All');
const activeSort = ref('latest');

// Modal State
const isCreateModalOpen = ref(false);

// Search debounce
let searchDebounceTimeout = null;

// Popular sidebar tags
const sidebarTags = [
  { name: 'Burger', icon: '🍔' },
  { name: 'Pizza', icon: '🍕' },
  { name: 'Biryani', icon: '🍛' },
  { name: 'Pasta', icon: '🍝' },
  { name: 'Coffee', icon: '☕' },
  { name: 'Dessert', icon: '🍰' },
  { name: 'Chinese', icon: '🥢' },
  { name: 'Fast Food', icon: '🍟' },
  { name: 'Steak', icon: '🥩' },
  { name: 'Kebab', icon: '🍢' },
];

// Load Feed
const loadFeed = async (page = 1, append = false) => {
  if (append) {
    isLoadingMore.value = true;
  } else {
    isLoading.value = true;
  }

  try {
    const res = await fetchReviews({
      search: search.value.trim() || undefined,
      tag: activeTag.value !== 'All' ? activeTag.value : undefined,
      sort: activeSort.value !== 'latest' ? activeSort.value : undefined,
      page,
      limit: 10,
    });

    if (append) {
      reviews.value = [...reviews.value, ...(res.data || [])];
    } else {
      reviews.value = res.data || [];
    }

    currentPage.value = res.current_page || 1;
    lastPage.value = res.last_page || 1;
    totalReviews.value = res.total || 0;
  } catch (err) {
    addToast(err.data?.message || 'Failed to load reviews feed', 'alert-error');
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
  }
};

onMounted(() => {
  loadFeed(1);
});

// Watch Filters
watch([activeTag, activeSort], () => {
  loadFeed(1);
});

watch(search, () => {
  clearTimeout(searchDebounceTimeout);
  searchDebounceTimeout = setTimeout(() => {
    loadFeed(1);
  }, 400);
});

// Load More
const loadMore = () => {
  if (currentPage.value < lastPage.value && !isLoadingMore.value) {
    loadFeed(currentPage.value + 1, true);
  }
};

// Optimistic Reaction Handler
const handleReact = async ({ reviewId, type }) => {
  const target = reviews.value.find((r) => r.id === reviewId);
  if (!target) return;

  const previousReaction = target.current_user_reaction;
  const prevStats = JSON.parse(JSON.stringify(target.stats || { total_reactions: 0, breakdown: {} }));

  // Optimistic UI Update
  if (type === 'none') {
    if (previousReaction && target.stats?.breakdown?.[previousReaction]) {
      target.stats.breakdown[previousReaction]--;
      target.stats.total_reactions--;
    }
    target.current_user_reaction = null;
  } else {
    if (!target.stats) {
      target.stats = { total_reactions: 0, breakdown: {} };
    }
    if (!target.stats.breakdown) {
      target.stats.breakdown = {};
    }

    // Decrement previous if existed
    if (previousReaction && target.stats.breakdown[previousReaction]) {
      target.stats.breakdown[previousReaction]--;
    } else {
      target.stats.total_reactions++;
    }

    // Increment new
    target.stats.breakdown[type] = (target.stats.breakdown[type] || 0) + 1;
    target.current_user_reaction = type;
  }

  try {
    const res = await reactToReview(reviewId, type);
    target.current_user_reaction = res.data?.current_user_reaction;
  } catch (err) {
    // Revert optimistic update
    target.current_user_reaction = previousReaction;
    target.stats = prevStats;
    addToast(err.data?.message || 'Failed to update reaction', 'alert-error');
  }
};

// Delete Review Handler
const handleDeleteReview = async (reviewId) => {
  try {
    await deleteReview(reviewId);
    reviews.value = reviews.value.filter((r) => r.id !== reviewId);
    totalReviews.value--;
    addToast('Review deleted successfully', 'alert-success');
  } catch (err) {
    addToast(err.data?.message || 'Failed to delete review', 'alert-error');
  }
};

// Handle New Review Created
const handleReviewCreated = (newReview) => {
  reviews.value.unshift(newReview);
  totalReviews.value++;
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// Open Create Review Modal with Auth Check
const openCreateModal = () => {
  if (!auth.isAuthenticated) {
    addToast('Please login to share your food review', 'alert-warning');
    navigateTo('/login');
    return;
  }
  isCreateModalOpen.value = true;
};
</script>

<template>
  <div class="min-h-screen py-4 sm:py-8 lg:py-10 px-3 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-5 sm:space-y-6">
    
    <!-- Hero Header & Community Banner -->
    <div class="bg-gradient-to-br from-primary/15 via-base-100 to-accent/15 p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl border border-base-200 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div class="space-y-1 sm:space-y-1.5">
        <div class="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/15 text-primary rounded-full text-xs font-bold">
          <span>✨ Foodie Community Feed</span>
        </div>
        <h1 class="text-xl sm:text-2xl md:text-3xl font-extrabold text-base-content tracking-tight">
          Discover & Share Food Reviews
        </h1>
        <p class="text-xs sm:text-sm text-base-content/70 max-w-xl">
          Explore genuine dish ratings, delicious photos, and restaurant recommendations from food lovers near you.
        </p>
      </div>

      <button
        type="button"
        class="btn btn-primary text-white shadow-lg shadow-primary/20 rounded-xl sm:rounded-2xl gap-2 shrink-0 self-start sm:self-center"
        @click="openCreateModal"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4.5 w-4.5 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        <span class="text-xs sm:text-sm">Write Review</span>
      </button>
    </div>

    <!-- Responsive Layout Grid (Single column on mobile/tablet, 2-column with sidebar on desktop) -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-8 items-start">
      
      <!-- Main Feed Column (Full width on mobile, 8 cols on large screens) -->
      <div class="lg:col-span-8 space-y-4 sm:space-y-6">
        
        <!-- Filter, Search & Tags Bar -->
        <ReviewFilterBar
          v-model:search="search"
          v-model:active-tag="activeTag"
          v-model:active-sort="activeSort"
        />

        <!-- Loading Skeletons -->
        <div v-if="isLoading" class="space-y-4">
          <div v-for="i in 3" :key="i" class="card bg-base-100 shadow-sm border border-base-200 p-4 sm:p-5 rounded-2xl sm:rounded-3xl animate-pulse space-y-4">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 sm:w-11 sm:h-11 bg-base-300 rounded-full" />
              <div class="space-y-2 flex-1">
                <div class="h-4 bg-base-300 rounded w-1/3" />
                <div class="h-3 bg-base-300 rounded w-1/4" />
              </div>
            </div>
            <div class="h-12 sm:h-14 bg-base-200 rounded-xl" />
            <div class="h-44 sm:h-64 bg-base-300 rounded-2xl w-full" />
          </div>
        </div>

        <!-- Feed List -->
        <div v-else-if="reviews.length" class="space-y-4 sm:space-y-6">
          <ReviewCard
            v-for="review in reviews"
            :key="review.id"
            :review="review"
            :current-user-id="auth.user?.id"
            @react="handleReact"
            @delete-review="handleDeleteReview"
            @filter-tag="(tag) => { activeTag = tag; }"
          />

          <!-- Pagination / Load More Button -->
          <div v-if="currentPage < lastPage" class="text-center pt-2 sm:pt-4">
            <button
              type="button"
              class="btn btn-outline btn-primary rounded-xl sm:rounded-2xl px-6 sm:px-8 text-xs sm:text-sm shadow-sm hover:text-white"
              :disabled="isLoadingMore"
              @click="loadMore"
            >
              <span v-if="isLoadingMore" class="loading loading-spinner loading-sm" />
              <span v-else>Load More Reviews ({{ totalReviews - reviews.length }} remaining)</span>
            </button>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="card bg-base-100 border border-base-200 shadow-sm rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center space-y-4">
          <div class="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-primary/10 rounded-full flex items-center justify-center text-3xl sm:text-4xl">
            🍜
          </div>
          <div class="space-y-1.5">
            <h3 class="font-bold text-base sm:text-lg text-base-content">No reviews found</h3>
            <p class="text-xs sm:text-sm text-base-content/60 max-w-sm mx-auto">
              {{ search || activeTag !== 'All' ? 'Try adjusting your search query or food tags to find what you are looking for.' : 'Be the first foodie to share a delicious review in our community!' }}
            </p>
          </div>
          <div class="pt-2">
            <button
              v-if="search || activeTag !== 'All'"
              type="button"
              class="btn btn-ghost btn-sm text-primary rounded-xl"
              @click="() => { search = ''; activeTag = 'All'; }"
            >
              Reset Filters
            </button>
            <button
              v-else
              type="button"
              class="btn btn-primary text-white rounded-xl sm:rounded-2xl px-6 btn-sm sm:btn-md"
              @click="openCreateModal"
            >
              Write First Review
            </button>
          </div>
        </div>

      </div>

      <!-- Right Desktop Sticky Sidebar (Visible on large screens) -->
      <aside class="hidden lg:block lg:col-span-4 space-y-5 sticky top-20">
        
        <!-- Post Review Prompt Card -->
        <div class="card bg-gradient-to-br from-primary/10 via-base-100 to-base-100 p-5 rounded-3xl border border-base-200 shadow-sm space-y-3.5">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-2xl bg-primary text-primary-content flex items-center justify-center text-xl shadow-md shadow-primary/20">
              📸
            </div>
            <div>
              <h3 class="font-bold text-sm text-base-content">Share Your Taste</h3>
              <p class="text-xs text-base-content/60">Help fellow food lovers decide</p>
            </div>
          </div>
          <p class="text-xs text-base-content/70 leading-relaxed">
            Tried a delicious dish recently? Upload photos, give your rating, and let everyone know what to order!
          </p>
          <button
            type="button"
            class="btn btn-primary text-white w-full rounded-2xl text-xs sm:text-sm shadow-md shadow-primary/20"
            @click="openCreateModal"
          >
            + Create New Review
          </button>
        </div>

        <!-- Trending Cuisines & Tags Card -->
        <div class="card bg-base-100 p-5 rounded-3xl border border-base-200 shadow-sm space-y-3.5">
          <div class="flex items-center justify-between">
            <h3 class="font-bold text-sm text-base-content flex items-center gap-2">
              <span>🔥</span>
              <span>Popular Cuisines</span>
            </h3>
            <span class="text-[11px] text-base-content/50">Click to filter</span>
          </div>

          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="t in sidebarTags"
              :key="t.name"
              type="button"
              class="badge py-2 px-3 rounded-xl text-xs font-medium cursor-pointer transition-all duration-200 border"
              :class="activeTag === t.name 
                ? 'badge-primary text-primary-content border-primary shadow-xs' 
                : 'badge-ghost bg-base-200/50 hover:bg-base-200 border-transparent text-base-content/70 hover:text-base-content'"
              @click="activeTag = t.name"
            >
              <span>{{ t.icon }}</span>
              <span class="ml-1">{{ t.name }}</span>
            </button>
          </div>
        </div>

        <!-- Community Guidelines & Trust Card -->
        <div class="card bg-base-100 p-5 rounded-3xl border border-base-200 shadow-sm space-y-3">
          <h3 class="font-bold text-sm text-base-content flex items-center gap-2">
            <span>🛡️</span>
            <span>Community Guidelines</span>
          </h3>
          <ul class="text-xs text-base-content/70 space-y-2 leading-relaxed">
            <li class="flex items-start gap-2">
              <span class="text-primary font-bold">✓</span>
              <span>Keep reviews authentic, honest, and helpful.</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-primary font-bold">✓</span>
              <span>Add real food photos to make your review stand out.</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-primary font-bold">✓</span>
              <span>Tag food items so others can discover new dishes.</span>
            </li>
          </ul>
        </div>

      </aside>

    </div>

    <!-- Floating Action Button (Mobile Only) -->
    <div class="fixed bottom-6 right-4 sm:hidden z-40">
      <button
        type="button"
        class="btn btn-primary btn-circle btn-lg text-white shadow-2xl shadow-primary/40 flex items-center justify-center active:scale-95 transition-transform"
        title="Share Review"
        @click="openCreateModal"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </div>

    <!-- Create Review Modal -->
    <CreateReviewModal
      :is-open="isCreateModalOpen"
      @close="isCreateModalOpen = false"
      @created="handleReviewCreated"
    />
  </div>
</template>